import React, { useMemo } from 'react'
import { formatPhoneNumber } from '@utils/numberFormatter.util'
import {
  ISinchAvailableNumber,
  SinchNumberType,
} from '@services/api/fax/numbers.api'
import {
  Alert,
  DataTable,
  SelectionBoxGroup,
  type DataTableColumn,
} from '@enamelai/design-system'

type NumberTableRow = { id: string }

interface SelectNumberProps {
  numberType: SinchNumberType | null
  setNumberType: (type: SinchNumberType | null) => void
  availableNumbers: ISinchAvailableNumber[]
  isNumbersLoading: boolean
  fetchError: string | null
  selectedNumber: ISinchAvailableNumber | null
  setSelectedNumber: (number: ISinchAvailableNumber | null) => void
}

const formatMonthly = (item: ISinchAvailableNumber): string => {
  const amount = item.monthlyPrice?.amount
  if (!amount) return '-'
  const num = Number(amount)
  return Number.isNaN(num) ? amount : `$${num.toFixed(2)}/mo`
}

const SelectNumber: React.FC<SelectNumberProps> = ({
  numberType,
  setNumberType,
  availableNumbers,
  isNumbersLoading,
  fetchError,
  selectedNumber,
  setSelectedNumber,
}) => {
  const numbersById = useMemo(() => {
    const map = new Map<string, ISinchAvailableNumber>()
    availableNumbers.forEach((item) => {
      map.set(item.phoneNumber, item)
    })
    return map
  }, [availableNumbers])

  const columns: DataTableColumn<NumberTableRow>[] = [
    {
      id: 'number',
      label: 'Number',
      renderCell: (row) => {
        const item = numbersById.get(row.id)
        return item ? formatPhoneNumber(item.phoneNumber, false) : row.id
      },
    },
    {
      id: 'monthly',
      label: 'Monthly fee',
      renderCell: (row) => {
        const item = numbersById.get(row.id)
        return item ? formatMonthly(item) : '—'
      },
    },
  ]

  return (
    <div className="scp-pm-card">
      <SelectionBoxGroup
        label="Number type"
        value={numberType ?? ''}
        onChange={(value) => setNumberType(value as SinchNumberType)}
        options={[
          {
            value: 'LOCAL',
            title: 'Local numbers',
            description: 'Area specific numbers',
          },
          {
            value: 'TOLL_FREE',
            title: 'Toll-free numbers',
            description: 'Nationwide non-geographic numbers',
          },
        ]}
      />
      {numberType && fetchError && (
        <Alert
          variant="critical"
          layout="board"
          title="Could not load numbers"
          description={fetchError}
          showActions={false}
          showMenu={false}
        />
      )}
      {numberType && !fetchError && (
        <DataTable<NumberTableRow>
          className="purchase-number-list"
          columns={columns}
          rows={availableNumbers.map((item) => ({ id: item.phoneNumber }))}
          selectable
          showActions={false}
          selectedRowIds={
            selectedNumber?.phoneNumber ? [selectedNumber.phoneNumber] : []
          }
          onSelectedRowIdsChange={(ids) => {
            const nextId = ids[ids.length - 1] ?? ''
            setSelectedNumber(numbersById.get(nextId) ?? null)
          }}
          state={
            isNumbersLoading
              ? 'loading'
              : availableNumbers.length === 0
                ? 'empty'
                : 'default'
          }
          emptyState={{
            title: 'No fax numbers available',
            description: 'No fax numbers available for this selection.',
          }}
        />
      )}
    </div>
  )
}

export default SelectNumber
